import { useStorage } from '@vueuse/core';
import { isEqual } from 'lodash';
import { DateTime } from 'luxon';
import { computed, watch } from 'vue';
import { LocationQueryValue, useRoute, useRouter } from 'vue-router';

import { PERFORMANCE_DASHBOARD_FILTERS_KEY } from '@/constants/storageKeys';

import useAuth from './useAuth';

const VALID_SINGLE_FILTER_VALUES = {
  coverageType: ['partners', 'engagement'],
  opportunityType: ['all', 'net-new', 'existing'],
  timePeriod: ['thisQuarter', 'lastQuarter', 'yearToDate'],
  sort: [
    'overlapCount',
    'engagementActivitiesCount',
    'partnerImpact',
    'attributionAmount',
    null,
  ],
} as const;

export type CoverageType =
  (typeof VALID_SINGLE_FILTER_VALUES.coverageType)[number];
export type OpportunityType =
  (typeof VALID_SINGLE_FILTER_VALUES.opportunityType)[number];
export type TimePeriod = (typeof VALID_SINGLE_FILTER_VALUES.timePeriod)[number];
export type SortField = (typeof VALID_SINGLE_FILTER_VALUES.sort)[number];
export type OpportunityOwnerType = string[];

export type PerformanceDashboardSingleValueFilters = {
  [K in keyof typeof VALID_SINGLE_FILTER_VALUES]: (typeof VALID_SINGLE_FILTER_VALUES)[K][number];
};

export type PerformanceDashboardFilters = {
  coverageType: CoverageType;
  opportunityType: OpportunityType;
  timePeriod: TimePeriod;
  sort: SortField;
  deal_owner_id: string[] | null;
};

type ValidFilterKeys = keyof PerformanceDashboardFilters;

const ARRAY_FILTER_PARAMS = ['deal_owner_id'] as const;

export const defaultFilters: PerformanceDashboardFilters = {
  coverageType: 'partners',
  opportunityType: 'all',
  timePeriod: 'thisQuarter',
  sort: null,
  deal_owner_id: null,
};

const VALID_KEYS = Object.keys(defaultFilters) as ValidFilterKeys[];

export function usePerformanceDashboardFilters() {
  const route = useRoute();
  const router = useRouter();
  const { currentOrg } = useAuth();

  const fiscalYearStart = computed(
    () => currentOrg.value?.fiscal_year_start ?? 1,
  );

  const filters = useStorage<PerformanceDashboardFilters>(
    PERFORMANCE_DASHBOARD_FILTERS_KEY,
    defaultFilters,
    sessionStorage,
    { mergeDefaults: true },
  );

  const updateFilters = (newFilters: Partial<PerformanceDashboardFilters>) => {
    filters.value = {
      ...filters.value,
      ...newFilters,
    };
  };

  const syncFromQuery = () => {
    if (route.path !== '/measure/ecosystem') return;

    const updatedQuery = {} as Record<
      keyof PerformanceDashboardFilters,
      LocationQueryValue | LocationQueryValue[]
    >;
    let needsUrlFix = false;
    const currentFilters = filters.value;

    for (const key of VALID_KEYS) {
      const raw = route.query[key];

      if (
        ARRAY_FILTER_PARAMS.includes(
          key as (typeof ARRAY_FILTER_PARAMS)[number],
        )
      ) {
        if (raw) {
          const arr = Array.isArray(raw) ? raw : [raw];
          if (!isEqual(arr, currentFilters[key])) {
            filters.value = {
              ...filters.value,
              [key]: arr as PerformanceDashboardFilters[typeof key],
            };
            updatedQuery[key] = arr as LocationQueryValue[];
          }
        } else {
          const fallback = currentFilters[key] ?? defaultFilters[key];
          filters.value = {
            ...filters.value,
            [key]: fallback as PerformanceDashboardFilters[typeof key],
          };
          updatedQuery[key] = fallback as LocationQueryValue;
          needsUrlFix = true;
        }
        continue;
      }

      const val = typeof raw === 'string' ? raw : undefined;
      const allowed = (
        VALID_SINGLE_FILTER_VALUES as Record<string, readonly string[]>
      )[key] as readonly string[];

      if (val && allowed.includes(val)) {
        filters.value = {
          ...filters.value,
          [key]: val as PerformanceDashboardFilters[typeof key],
        };
        updatedQuery[key] = val;
      } else {
        const fallback = filters.value[key] ?? defaultFilters[key];
        filters.value = {
          ...filters.value,
          [key]: fallback as PerformanceDashboardFilters[typeof key],
        };
        updatedQuery[key] = fallback;
        needsUrlFix = true;
        continue;
      }
    }

    if (needsUrlFix || Object.keys(route.query).length === 0) {
      router.replace({ query: updatedQuery });
    }
  };

  syncFromQuery();

  watch(
    filters,
    (newVal) => {
      const query: Record<string, LocationQueryValue | LocationQueryValue[]> = {
        ...route.query,
      };
      let changed = false;

      (
        Object.keys(newVal) as (keyof PerformanceDashboardSingleValueFilters)[]
      ).forEach((key) => {
        const raw = route.query[key];
        if (
          raw &&
          ARRAY_FILTER_PARAMS.includes(
            key as (typeof ARRAY_FILTER_PARAMS)[number],
          )
        ) {
          const asArray = Array.isArray(newVal[key])
            ? newVal[key]
            : [newVal[key]];

          if (isEqual(raw, asArray)) return;
          query[key] = asArray;
          changed = true;
          return;
        }
        if (query[key] !== newVal[key]) {
          query[key] = newVal[key];
          changed = true;
        }
      });

      if (changed) {
        router.replace({ query });
      }
    },
    { deep: true },
  );

  const formattedDates = computed(() => {
    const now = DateTime.local();
    let start: DateTime;
    let end: DateTime | undefined = undefined;
    const fiscalYearStartVal = fiscalYearStart.value;

    switch (filters.value.timePeriod) {
      case 'thisQuarter': {
        const quarterStartMonth =
          Math.floor((now.month - fiscalYearStartVal) / 3) * 3 +
          fiscalYearStartVal;
        start = DateTime.local()
          .set({ month: quarterStartMonth })
          .startOf('month');
        break;
      }
      case 'lastQuarter': {
        const lastQuarterStartMonth =
          Math.floor((now.month - fiscalYearStartVal - 3) / 3) * 3 +
          fiscalYearStartVal;
        start = DateTime.local()
          .set({ month: lastQuarterStartMonth })
          .startOf('month');
        end = start.plus({ months: 3 });
        break;
      }
      case 'yearToDate': {
        const fiscalStart = DateTime.local()
          .set({ month: fiscalYearStartVal })
          .startOf('month');
        start =
          fiscalStart > now ? fiscalStart.minus({ years: 1 }) : fiscalStart;
        break;
      }
      default: {
        start = now;
        end = now;
      }
    }

    return {
      startDate: start.toFormat('yyyy-MM'),
      endDate: end?.toFormat('yyyy-MM'),
    };
  });

  const fiscalYearStartDate = computed(() => {
    const now = DateTime.local();
    const fiscalYearStartVal = fiscalYearStart.value;

    const fiscalStart = DateTime.local()
      .set({ month: fiscalYearStartVal })
      .startOf('month');

    return (
      fiscalStart > now ? fiscalStart.minus({ years: 1 }) : fiscalStart
    ).toFormat('yyyy-MM');
  });

  return { filters, updateFilters, formattedDates, fiscalYearStartDate };
}
